// /electionday statistics layer.
//
// Inputs: per-district batches of clean reads (PL / PN / abstained)
// emitted by BallotReadPanel as the editor processes footage.
//
// Outputs: pooled national PL share + Wilson 95% CI, per-district
// roll-up, abstain rate, districts-covered count, the headline call
// state, and "how many more clean reads to projected call" — every
// number the StatsPanel needs to render.
//
// Sampling design (deliberate, do not bolt on inflation):
//   • Districts are treated as equal strata until real eligible-voter
//     counts arrive. Equal-strata + random box distribution inside a
//     count means the pooled clean-read fraction is an unbiased
//     estimator of the national PL share at no extra variance.
//   • There is therefore NO design-effect inflation; the Wilson
//     interval on the pooled proportion is used as-is. If/when
//     unequal stratum sizes arrive, switch this to a stratified
//     proportion estimator (sum N_h × p_h / sum N_h) and weight the
//     variance accordingly — the data shape is already district-keyed
//     so swapping the estimator is a one-function change.
//
// Acceptance rule (matches BallotCard.decide): a still is "clean"
// only when the verdict is PL or PN at HIGH confidence. Everything
// else — OTHER, UNREADABLE, medium/low confidence, failed rectify —
// is "abstained". The abstain rate is the headline diagnostic of
// sample quality and is shown alongside the tally, never folded in.

// `Batch` is the wire shape that crosses the admin → public boundary
// via the electionday_state Postgres row (kind='sampling'). The
// canonical declaration lives in src/data/electionday-sampling-types.ts
// so the API handler can import it without reaching into a component
// file; stats.ts re-exports it so existing call sites
// (`import { Batch } from './stats'`) don't have to change.
import type { Batch } from '../../data/electionday-sampling-types';
export type { Batch };

export interface DistrictTally {
  district: string;
  pl: number;
  pn: number;
  abstained: number;
  /** Pl + pn (the count that enters the national share). */
  clean: number;
  /** Pl + pn + abstained — the count of stills the editor processed. */
  totalStills: number;
  /** Eligible voters for this district. Placeholder
   *  (NATIONAL_ELIGIBLE_VOTERS / 13) until the editor wires in
   *  official figures from the electoral commission. */
  eligibleVoters: number;
  /** Official ballots-cast count in that district, when it lands —
   *  null until provided so the UI can render "—" instead of 0. */
  turnoutBallots: number | null;
}

// ── Constants ──────────────────────────────────────────────────────

/** 85% of 355,075 — the working national projection from votes.ts. */
export const NATIONAL_ELIGIBLE_VOTERS = 355_075;
export const TOTAL_DISTRICTS = 13;
export const EQUAL_STRATUM_SIZE = Math.round(
  NATIONAL_ELIGIBLE_VOTERS / TOTAL_DISTRICTS,
);

/** 95% confidence z-score. Surfaced so the methodology note can
 *  reference the same constant rather than hard-coding 1.96. */
export const Z_95 = 1.96;

/** Below this many pooled clean reads, the model is too noisy for
 *  a call. The "DIRECTIONAL — INDICATIVE" gate. */
export const MIN_CLEAN_DIRECTIONAL = 150;

/** Pooled clean reads required (with a ≥ ~9pp gap) before the
 *  Wilson interval is tight enough to bless a projected call. */
export const MIN_CLEAN_PROJECTED = 800;

/** District-coverage gate — half-plus-one. Even with abundant reads,
 *  if they all came from < 7 districts the sample isn't a national
 *  picture and we refuse to call. */
export const MIN_DISTRICTS_COVERED = 7;

/** Absolute distance from 50/50 (in proportion units, not %) below
 *  which we refuse to project a call even with sufficient reads.
 *  0.045 corresponds to a ~9-percentage-point lead, the spec's
 *  reference gap at the ~800-read PROJECTED CALL threshold. */
export const PROJECTED_MIN_GAP = 0.045;

// ── Wilson interval ────────────────────────────────────────────────

export interface WilsonInterval {
  point: number;
  lo: number;
  hi: number;
}

/** Wilson score interval for `successes` out of `trials`. Returns
 *  the point estimate alongside the bounds so callers can render
 *  the centre + the CI from a single object. Zero-trials returns
 *  an all-zero interval rather than NaN. */
export function wilsonInterval(
  successes: number,
  trials: number,
  z: number = Z_95,
): WilsonInterval {
  if (trials <= 0 || successes < 0) return { point: 0, lo: 0, hi: 0 };
  const p = successes / trials;
  const z2 = z * z;
  const denom = 1 + z2 / trials;
  const centre = (p + z2 / (2 * trials)) / denom;
  const margin =
    (z * Math.sqrt((p * (1 - p)) / trials + z2 / (4 * trials * trials))) /
    denom;
  return {
    point: p,
    lo: Math.max(0, centre - margin),
    hi: Math.min(1, centre + margin),
  };
}

// ── Aggregation ────────────────────────────────────────────────────

export type CallState = 'INSUFFICIENT' | 'DIRECTIONAL' | 'PROJECTED';

export interface NationalStats {
  /** Total clean PL reads pooled across real districts. */
  pl: number;
  /** Total clean PN reads pooled across real districts. */
  pn: number;
  /** pl + pn — denominator for the share. */
  clean: number;
  /** Stills that did NOT make it into the clean count. */
  abstained: number;
  /** clean + abstained — the count of stills the editor processed. */
  totalStills: number;
  /** Measured (clean / totalStills). Drives the "stills-to-call"
   *  projection — explicitly not an assumed value. */
  acceptRate: number;
  /** PL's share of clean reads. The headline number when defined. */
  plShare: number;
  /** Wilson 95% interval on plShare. */
  plInterval: WilsonInterval;
  /** Same, on PN's share among clean reads — derivable from plInterval
   *  by 1 - p but kept explicit so the UI doesn't have to flip signs. */
  pnInterval: WilsonInterval;
  /** abstained / totalStills. Shown as prominently as the tally — it
   *  is the most informative single diagnostic of sample quality. */
  abstainRate: number;
  /** Number of distinct real (non-TBC) districts seen at least once. */
  districtsCovered: number;
  /** District roll-ups, sorted naturally (District 1, District 2, …). */
  byDistrict: DistrictTally[];
  /** The call-state classification. */
  callState: CallState;
  /** Pooled clean reads still needed to reach the PROJECTED-CALL
   *  threshold. Zero once `clean ≥ MIN_CLEAN_PROJECTED`. */
  cleanReadsToProjected: number;
  /** Stills the editor must still process to convert
   *  `cleanReadsToProjected` more clean reads — derived from
   *  `acceptRate`, never an assumed constant. Null when accept rate
   *  is undefined (no stills processed yet). */
  stillsToProjected: number | null;
  /** Whichever of PL / PN has more clean reads, or null at a tie. */
  leader: 'PL' | 'PN' | null;
  /** TBC batches the editor has not yet assigned a district to —
   *  surfaced so the UI can prompt for the missing label rather
   *  than silently dropping them from the national rollup. */
  tbcStills: number;
}

/** Aggregate a list of batches (committed + the current, uncommitted
 *  one if any) into the full StatsPanel readout. */
export function computeStats(batches: Batch[]): NationalStats {
  const byDistrictMap = new Map<string, DistrictTally>();
  let tbcStills = 0;

  for (const b of batches) {
    if (b.district === 'TBC') {
      tbcStills += b.pl + b.pn + b.abstained;
      continue;
    }
    const existing = byDistrictMap.get(b.district);
    if (existing) {
      existing.pl += b.pl;
      existing.pn += b.pn;
      existing.abstained += b.abstained;
      existing.clean = existing.pl + existing.pn;
      existing.totalStills = existing.clean + existing.abstained;
    } else {
      const clean = b.pl + b.pn;
      byDistrictMap.set(b.district, {
        district: b.district,
        pl: b.pl,
        pn: b.pn,
        abstained: b.abstained,
        clean,
        totalStills: clean + b.abstained,
        eligibleVoters: EQUAL_STRATUM_SIZE,
        turnoutBallots: null,
      });
    }
  }

  const byDistrict = Array.from(byDistrictMap.values()).sort((a, b) =>
    a.district.localeCompare(b.district, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );

  let pl = 0;
  let pn = 0;
  let abstained = 0;
  for (const d of byDistrict) {
    pl += d.pl;
    pn += d.pn;
    abstained += d.abstained;
  }
  const clean = pl + pn;
  const totalStills = clean + abstained;
  const acceptRate = totalStills > 0 ? clean / totalStills : 0;
  const plShare = clean > 0 ? pl / clean : 0;
  const plInterval = wilsonInterval(pl, clean);
  const pnInterval = wilsonInterval(pn, clean);
  const abstainRate = totalStills > 0 ? abstained / totalStills : 0;
  const districtsCovered = byDistrict.length;
  const leader = clean === 0 ? null : pl > pn ? 'PL' : pn > pl ? 'PN' : null;

  // ── Call-state classification ──
  // INSUFFICIENT is the default: < 150 pooled clean reads, < 7
  // districts, or the 95% Wilson interval still straddles 50/50.
  // DIRECTIONAL: above those thresholds AND the interval is wholly
  // on one side of 50%. PROJECTED tightens to ≥ 800 reads AND a
  // ≥ ~9pp gap (i.e. the lead margin is genuinely large, not a
  // statistical fluke at low N).
  const onOneSide = plInterval.lo > 0.5 || plInterval.hi < 0.5;
  const gap = Math.abs(plShare - 0.5);
  let callState: CallState = 'INSUFFICIENT';
  if (
    clean >= MIN_CLEAN_DIRECTIONAL &&
    districtsCovered >= MIN_DISTRICTS_COVERED &&
    onOneSide
  ) {
    callState = 'DIRECTIONAL';
    if (clean >= MIN_CLEAN_PROJECTED && gap >= PROJECTED_MIN_GAP) {
      callState = 'PROJECTED';
    }
  }

  const cleanReadsToProjected = Math.max(0, MIN_CLEAN_PROJECTED - clean);
  const stillsToProjected =
    acceptRate > 0 ? Math.ceil(cleanReadsToProjected / acceptRate) : null;

  return {
    pl,
    pn,
    clean,
    abstained,
    totalStills,
    acceptRate,
    plShare,
    plInterval,
    pnInterval,
    abstainRate,
    districtsCovered,
    byDistrict,
    callState,
    cleanReadsToProjected,
    stillsToProjected,
    leader,
    tbcStills,
  };
}
